import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { Components } from 'react-markdown'
import styles from './MessageBubble.module.scss'

const truncateUrl = (url: string, maxLength: number = 50): string => {
  if (url.length <= maxLength) return url
  const parts = url.split('/')
  const lastPart = parts[parts.length - 1] || ''
  if (lastPart && lastPart.includes('.') && lastPart.length < url.length) {
    const extension = lastPart.split('.').pop() || ''
    const nameWithoutExt = lastPart.substring(0, lastPart.lastIndexOf('.'))
    const maxNameLength = maxLength - extension.length - 4
    if (nameWithoutExt.length > 0 && extension.length > 0 && extension.length < 10) {
      if (lastPart.length <= maxLength) return lastPart
      if (nameWithoutExt.length <= maxNameLength) return lastPart
      return `${nameWithoutExt.substring(0, maxNameLength)}...${extension}`
    }
  }
  return `${url.substring(0, maxLength - 3)}...`
}

const formatHref = (href: string): string => {
  if (href.startsWith('http://') || href.startsWith('https://')) return href
  if (href.startsWith('www.')) return `https://${href}`
  if (href.startsWith('mailto:')) return href
  return href
}

const extractTextChildren = (children: React.ReactNode): string => {
  if (typeof children === 'string' || typeof children === 'number') return String(children)
  if (Array.isArray(children)) return children.map(extractTextChildren).join('')
  if (React.isValidElement(children) && children.props && 'children' in children.props) {
    return extractTextChildren((children.props as { children?: React.ReactNode }).children)
  }
  return ''
}

const markdownComponents = (): Components => ({
  p: ({ children }) => <p className={styles.mdParagraph}>{children}</p>,
  h1: ({ children }) => <h2 className={styles.mdHeading}>{children}</h2>,
  h2: ({ children }) => <h2 className={styles.mdHeading}>{children}</h2>,
  h3: ({ children }) => <h3 className={styles.mdHeading3}>{children}</h3>,
  strong: ({ children }) => <strong className={styles.boldText}>{children}</strong>,
  em: ({ children }) => <em className={styles.mdEm}>{children}</em>,
  ul: ({ children }) => <ul className={styles.mdList}>{children}</ul>,
  ol: ({ children }) => <ol className={styles.mdOrderedList}>{children}</ol>,
  li: ({ children }) => <li className={styles.mdListItem}>{children}</li>,
  a: ({ href, children }) => {
    const h = href ? formatHref(href) : ''
    const label = extractTextChildren(children).trim() || h
    const display = /^https?:\/\//i.test(label) || label.startsWith('www.') ? truncateUrl(label, 50) : label
    return (
      <a
        href={h || '#'}
        className={styles.messageLink}
        target="_blank"
        rel="noopener noreferrer"
        title={h}
      >
        {display}
      </a>
    )
  },
  code: ({ className, children, ...props }) => (
    <code className={`${styles.mdCode} ${className || ''}`} {...props}>
      {children}
    </code>
  ),
  pre: ({ children }) => <pre className={styles.mdPre}>{children}</pre>,
  blockquote: ({ children }) => <blockquote className={styles.mdBlockquote}>{children}</blockquote>
})

export const formatMessageText = (text: string, messageId: string): React.ReactNode => {
  const raw = String(text ?? '')
  if (!raw.trim()) return null

  return (
    <div className={styles.markdownBody} data-message-format={messageId}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents()}>
        {raw.trimEnd()}
      </ReactMarkdown>
    </div>
  )
}
